import { Button, Typography } from '@mui/material';

import { useAuthOptional } from '../auth/AuthContext';
import AuthPageShell from './AuthPageShell';

/**
 * What `/reset-password` becomes under a passwordless provider (US-009).
 *
 * <h2>Why the route stays registered instead of redirecting</h2>
 *
 * Reset emails sent BEFORE the cutover keep landing here for as long as their
 * links live, and a bookmarked "forgot password" page outlives any campaign. A
 * redirect to `/` would bounce those visitors straight into the hosted sign-in
 * page with no explanation of why the thing they clicked vanished — and, for
 * anyone mid-recovery, into the same loop they were trying to escape. So the
 * route answers, and what it says is the actual way in.
 *
 * <h2>What it must never say</h2>
 *
 * No Keycloak password was migrated to Scalekit — nothing was copied, and the
 * backend reads no hash anywhere (`PasswordFlowPolicy`). Copy here therefore
 * never implies an old password still works, or that one "moved". It names the
 * two real options instead, in the same words the backend's
 * `POST /api/auth/forgot-password` answers with, so a user who sees both sees
 * one message.
 *
 * <h2>No network call</h2>
 *
 * There is nothing to ask the server. This page holds no form, issues no
 * request, and cannot fail.
 */
// Byte-for-byte the string `ForgotPasswordResource.PASSWORDLESS_GUIDANCE`
// returns (straight apostrophe, em dash), so a user who triggers the API and a
// user who lands on this page read one sentence, not two near-identical ones.
export const PASSWORDLESS_RECOVERY_MESSAGE =
  "Lutiq no longer uses passwords. Go to the sign-in page and choose "
  + "Continue with Google, or Email me a code — we'll send a one-time code "
  + 'to your address.';

const PasswordlessRecoveryPage = () => {
  const auth = useAuthOptional();

  const handleBackToSignIn = () => {
    if (!auth) {
      window.location.href = '/';
      return;
    }
    // Provider-neutral hosted-auth entry (US-008 seam). Under Scalekit this is
    // the hosted sign-in page offering Google and the one-time email code; the
    // method name predates the cutover and does NOT mean "password login".
    void auth.loginWithPassword('/');
  };

  return (
    <AuthPageShell eyebrow="Account recovery" title="No password needed">
      <Typography color="text.secondary">{PASSWORDLESS_RECOVERY_MESSAGE}</Typography>
      <Button variant="contained" size="large" onClick={handleBackToSignIn}>
        Back to sign in
      </Button>
    </AuthPageShell>
  );
};

export default PasswordlessRecoveryPage;
